import { useQuery } from "@tanstack/react-query";
import { Alert, Spin } from "antd";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import BackIcon from "src/assets/svgs/back-icon.svg";
import { CustomTitleSection } from "src/components/ui";
import { CustomSvgIcon } from "sipli-fleet-core/components";
import { getManagedPrivacyPolicy } from "src/services/privacyPolicyService";

const SETTINGS_TABS = ["privacy_policy"] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];

type PolicyBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; id: string; text: string; level: number };

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

const parsePolicyContent = (content: string): PolicyBlock[] => {
  const blocks: PolicyBlock[] = [];
  const paragraphLines: string[] = [];
  const slugCounts = new Map<string, number>();

  const flushParagraph = () => {
    if (paragraphLines.length) {
      blocks.push({ type: "paragraph", text: paragraphLines.join(" ") });
      paragraphLines.length = 0;
    }
  };

  content.split(/\r?\n/).forEach((line) => {
    const match = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (match) {
      flushParagraph();
      const text = match[2].trim();
      const baseId =
        text
          .toLowerCase()
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || "section";
      const count = slugCounts.get(baseId) ?? 0;
      slugCounts.set(baseId, count + 1);
      blocks.push({
        type: "heading",
        id: count ? `${baseId}-${count + 1}` : baseId,
        text,
        level: match[1].length,
      });
    } else if (line.trim()) {
      paragraphLines.push(line.trim());
    } else {
      flushParagraph();
    }
  });

  flushParagraph();
  return blocks;
};

const PrivacyPolicyTab = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["PRIVACY_POLICY_LATEST", language],
    queryFn: () => getManagedPrivacyPolicy(language),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const content = data?.content ?? "";
  if (isLoading)
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  if (isError) return <Alert type="error" message={t("privacy_policy_load_error")} />;
  if (!content.trim()) return <Alert type="info" message={t("privacy_policy_not_published")} />;

  const blocks = parsePolicyContent(content);
  const title =
    blocks.find((block) => block.type === "heading" && block.level === 1)?.text ??
    t("privacy_policy_title");
  const articleBlocks = blocks.filter((block) => !(block.type === "heading" && block.level === 1));
  const indexItems = articleBlocks.filter((block) => block.type === "heading" && block.level > 1);
  const nextSectionIndex = articleBlocks.findIndex(
    (block, index) => index > 0 && block.type === "heading" && block.level === 2
  );
  const firstSectionBlocks = articleBlocks.slice(
    0,
    nextSectionIndex === -1 ? articleBlocks.length : nextSectionIndex
  );
  const remainingBlocks = nextSectionIndex === -1 ? [] : articleBlocks.slice(nextSectionIndex);
  const renderBlocks = (contentBlocks: PolicyBlock[]) =>
    contentBlocks.map((block, blockIndex) => {
      if (block.type === "paragraph") {
        return (
          <p key={`${block.type}-${blockIndex}`} className="mb-3 text-[14px]">
            {block.text}
          </p>
        );
      }

      const Heading = `h${Math.min(block.level, 6)}` as HeadingTag;
      const isSection = block.level === 2;

      return (
        <Heading
          key={block.id}
          id={block.id}
          className={`${isSection ? "mt-7 text-[18px] text-[#17191f]" : "mt-4 text-[16px] text-gray10"} mb-3 scroll-mt-4 font-bold`}
          style={
            isSection
              ? undefined
              : {
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  fontSize: "15.75px",
                  lineHeight: "20px",
                }
          }
        >
          {block.text}
        </Heading>
      );
    });

  return (
    <div className="flow-root">
      <h3 className="mb-6 text-[20px] font-bold leading-7">{title}</h3>
      <div className="grid gap-6 md:grid-cols-2 md:items-start">
        <article className="text-[14px] leading-5 text-[#282e40]">
          {renderBlocks(firstSectionBlocks)}
        </article>
        <nav
          aria-label={t("privacy_policy_index")}
          className="rounded-md border border-[#d9dce5] bg-table-row-alt-bg p-5"
        >
          <h4 className="mb-4 text-[18px] font-bold">{t("privacy_policy_index")}</h4>
          <ol className="space-y-2">
            {indexItems.map((item) => (
              <li key={item.id} className={item.level > 2 ? "ml-4" : undefined}>
                <a
                  href={`#${item.id}`}
                  className="text-primary hover:underline"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "18px",
                    letterSpacing: "0.05px",
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById(item.id)?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </div>
      {remainingBlocks.length > 0 && (
        <article className="text-[14px] leading-5 text-[#282e40]">
          {renderBlocks(remainingBlocks)}
        </article>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const activeTab: SettingsTab = SETTINGS_TABS.includes(requestedTab as SettingsTab)
    ? (requestedTab as SettingsTab)
    : "privacy_policy";

  const selectTab = (tab: SettingsTab) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", tab);
    navigate(
      { pathname: location.pathname, search: `?${searchParams.toString()}` },
      { replace: true }
    );
  };

  return (
    <section>
      <header className="flex gap-8 py-3">
        <div className="flex flex-1 items-center gap-5">
          <CustomSvgIcon src={BackIcon} className="cursor-pointer" onClick={() => navigate(-1)} />
          <CustomTitleSection
            titleKey="settings"
            level={1}
            classNameContainer="py-0 w-auto justify-start"
          />
        </div>
      </header>
      <div className="flex flex-col gap-6 pb-6 md:flex-row md:gap-8">
        <nav aria-label={t("settings")} className="w-full shrink-0 rounded-lg bg-white p-4 md:w-69">
          <div className="flex flex-col gap-2">
            {SETTINGS_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                aria-current={activeTab === tab ? "page" : undefined}
                onClick={() => selectTab(tab)}
                className={`flex h-12.5 w-full items-center rounded px-4 text-left text-[14px] font-medium ${activeTab === tab ? "bg-[#EBF0FB] text-[#3867D6]" : "text-[#4D536B]"}`}
              >
                {t("privacy_policy_title")}
              </button>
            ))}
          </div>
        </nav>
        <main className="min-w-0 flex-1 overflow-auto rounded-lg bg-white p-6">
          <PrivacyPolicyTab />
        </main>
      </div>
    </section>
  );
};

export default SettingsPage;
